import { useState, useRef, useEffect } from 'react';
import { shelfDragFlag } from '../lib/gestureFlags.js';
// Nel testo verticale della costina, il browser ruota di 90° (in senso
// orario) le lettere normali per farle leggere di lato — ma le emoji, per
// convenzione Unicode, restano dritte per conto loro, e finiscono storte
// rispetto al resto del titolo. Qui si spezza il titolo carattere per
// carattere (gestendo bene anche le emoji "composte" da più codepoint) e si
// applica la STESSA rotazione anche alle emoji, così seguono l'orientamento
// delle lettere invece di restare dritte per conto loro.
function verticalTitleParts(title){
  if(!title) return [];
  const graphemes=(typeof Intl!=="undefined"&&Intl.Segmenter)
    ? Array.from(new Intl.Segmenter(undefined,{granularity:"grapheme"}).segment(title),s=>s.segment)
    : Array.from(title);
  const emojiRe=/\p{Extended_Pictographic}/u;
  return graphemes.map((g,i)=>({text:g,emoji:emojiRe.test(g),key:i}));
}
export function BookSpine({item,isDragging,dragX}){
  const isAnnual=item.kind==="annual";
  const fillPct=Math.min(1,(item.filled||0)/(item.max||1));
  // Annual tomes are the tall, wide books; micro-libri are visibly smaller —
  // a shelf of mixed sizes reads as a real, lived-in library, not a UI grid.
  const baseW=isAnnual?(item.isActive?27:17):22;
  const extraW=fillPct*(isAnnual?11:10);
  const width=baseW+extraW;
  const height=isAnnual?(item.isActive?104:Math.round(80+fillPct*10)):Math.round(72+fillPct*16);
  const lean=item.leanSeed;
  return(
    <div style={{
      position:"relative",width,height,flexShrink:0,
      transform:`translateX(${dragX||0}px) rotate(${lean*0.6}deg) ${isDragging?"scale(1.09)":"scale(1)"}`,
      marginBottom:isAnnual?(item.isActive?0:(2-fillPct*2)):0,
      zIndex:isDragging?30:1,
      transition:isDragging?"none":"transform .18s ease",
      touchAction:"pan-x",
    }}>
      <div style={{
        width:"100%",height:"100%",borderRadius:isAnnual?"2px 3px 0 0":"1px 4px 0 0",
        background:item.spineBg,
        boxShadow:`inset 2px 0 5px rgba(0,0,0,.32), inset -1.5px 0 4px rgba(255,255,255,.18), 0 3px 7px rgba(0,0,0,.4), ${isDragging?"0 10px 20px rgba(0,0,0,.55)":item.isActive?`0 0 14px ${item.accent}66`:"none"}`,
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"space-between",
        padding:isAnnual?"8px 0 6px":"6px 0 5px",position:"relative",overflow:"hidden",
      }}>
        <div style={{position:"absolute",top:isAnnual?6:4,left:0,right:0,height:1,background:"rgba(255,255,255,.25)"}}/>
        <div style={{position:"absolute",bottom:isAnnual?6:4,left:0,right:0,height:1,background:"rgba(0,0,0,.25)"}}/>
        {isAnnual?(
          <>
            <div style={{writingMode:"vertical-rl",transform:"rotate(180deg)",fontSize:item.isActive?12:10,fontFamily:"Georgia,serif",color:"rgba(255,255,255,.96)",fontWeight:800,textShadow:"0 1px 1px rgba(0,0,0,.45)",letterSpacing:.5}}>{item.year}</div>
            <div style={{fontSize:item.isActive?10:8,filter:"drop-shadow(0 1px 1px rgba(0,0,0,.4))"}}>{item.emoji}</div>
          </>
        ):(
          <>
            {/* Nome del micro-libro/condiviso lungo la costina, verticale come
                l'anno del libro annuale — senza, due libri con lo stesso tema
                colore sono indistinguibili a colpo d'occhio. Se il titolo è
                troppo lungo per lo spazio disponibile, viene troncato con
                puntini di sospensione invece di sforare la costina. */}
            {item.title&&<div style={{writingMode:"vertical-rl",transform:"rotate(180deg)",fontSize:8.5,fontFamily:"Inter,sans-serif",fontWeight:700,color:"rgba(255,255,255,.96)",textShadow:"0 1px 1px rgba(0,0,0,.45)",letterSpacing:.2,maxHeight:42,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {verticalTitleParts(item.title).map(p=>p.emoji
                ? <span key={p.key} style={{display:"inline-block",transform:"rotate(90deg)"}}>{p.text}</span>
                : p.text
              )}
            </div>}
            <div style={{fontSize:9,filter:"drop-shadow(0 1px 1px rgba(0,0,0,.4))"}}>{item.emoji}</div>
          </>
        )}
        {item.badge&&<div style={{position:"absolute",top:2,right:1,fontSize:7,filter:"drop-shadow(0 1px 1px rgba(0,0,0,.5))"}}>{item.badge}</div>}
      </div>
      <div style={{position:"absolute",bottom:-3,left:1,right:1,height:5,background:"radial-gradient(ellipse,rgba(0,0,0,.4) 0%,transparent 75%)",filter:"blur(1px)"}}/>
    </div>
  );
}
export function ShelfOrnament({emoji,size=30}){
  return(
    <div style={{flexShrink:0,width:size,height:size*1.3,display:"flex",alignItems:"flex-end",justifyContent:"center",fontSize:size*0.62,filter:"drop-shadow(0 3px 3px rgba(0,0,0,.4))"}}>{emoji}</div>
  );
}
export function FreeDecoration({deco,pos,onMove,editable}){
  const wrapRef=useRef(null);
  const dragging=useRef(false);
  const [live,setLive]=useState(null);
  const onDown=(e)=>{
    if(!editable) return;
    dragging.current=true;
    if(e.target.setPointerCapture) try{e.target.setPointerCapture(e.pointerId);}catch(err){}
  };
  const computePercent=(clientX,clientY)=>{
    const parent=wrapRef.current&&wrapRef.current.parentElement;
    if(!parent) return null;
    const rect=parent.getBoundingClientRect();
    const x=Math.max(2,Math.min(98,((clientX-rect.left)/rect.width)*100));
    const y=Math.max(2,Math.min(98,((clientY-rect.top)/rect.height)*100));
    return {x,y};
  };
  const onPointerMoveHandler=(e)=>{
    if(!dragging.current) return;
    if(e.cancelable) e.preventDefault();
    const p=computePercent(e.clientX,e.clientY);
    if(p) setLive(p);
  };
  const onUp=()=>{
    if(!dragging.current) return;
    dragging.current=false;
    if(live&&onMove) onMove(deco.id,live.x,live.y);
    setLive(null);
  };
  const x=live?live.x:(pos?pos.x:50);
  const y=live?live.y:(pos?pos.y:50);
  return(
    <div ref={wrapRef}
      onPointerDown={onDown}
      onPointerMove={onPointerMoveHandler}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      title={deco.name}
      style={{
        position:"absolute",left:`${x}%`,top:`${y}%`,transform:`translate(-50%,-50%) ${live?"scale(1.15)":"scale(1)"}`,
        fontSize:26,cursor:editable?"grab":"default",zIndex:25,
        filter:"drop-shadow(0 3px 4px rgba(0,0,0,.55))",
        touchAction:"none",transition:live?"none":"transform .15s ease",pointerEvents:editable?"auto":"none",
      }}>
      {deco.emoji}
    </div>
  );
}
export function Bookshelf({items,dividers,editable,onReorder,onItemClick,decorations,decorationPositions,onDecorationMove,layoutEditMode}){
  const keyOrder=items.map(i=>i.key).join(",");
  const [liveOrder,setLiveOrder]=useState(()=>items.map(i=>i.key));
  const [dragKey,setDragKey]=useState(null);
  const [dragX,setDragX]=useState(0);
  const itemRefs=useRef({});
  const drag=useRef({startX:0,timer:null,active:false});
  useEffect(()=>{ setLiveOrder(items.map(i=>i.key)); },[keyOrder]);
  const itemsByKey={};
  items.forEach(i=>{ itemsByKey[i.key]=i; });
  const ordered=liveOrder.map(k=>itemsByKey[k]).filter(Boolean);
  const endDrag=(commit)=>{
    clearTimeout(drag.current.timer);
    if(drag.current.active&&commit&&onReorder) onReorder(liveOrder);
    if(drag.current.active){ shelfDragFlag.justDragged=true; setTimeout(()=>{shelfDragFlag.justDragged=false;},300); }
    drag.current.active=false;
    shelfDragFlag.active=false;
    setDragKey(null);
    setDragX(0);
  };
  const onDown=(key,e)=>{
    if(!editable) return;
    drag.current.startX=e.clientX;
    drag.current.active=false;
    clearTimeout(drag.current.timer);
    drag.current.timer=setTimeout(()=>{
      drag.current.active=true;
      shelfDragFlag.active=true;
      setDragKey(key);
      if(e.target.setPointerCapture) try{e.target.setPointerCapture(e.pointerId);}catch(err){}
    },480);
  };
  const onMove=(key,e)=>{
    if(!drag.current.active){
      if(Math.abs(e.clientX-drag.current.startX)>10) clearTimeout(drag.current.timer);
      return;
    }
    e.stopPropagation();
    if(e.cancelable) e.preventDefault();
    const dx=e.clientX-drag.current.startX;
    setDragX(dx);
    const myRect=itemRefs.current[key]&&itemRefs.current[key].getBoundingClientRect();
    if(!myRect) return;
    const myCenter=myRect.left+myRect.width/2+dx;
    const idx=liveOrder.indexOf(key);
    const rightKey=liveOrder[idx+1];
    if(rightKey&&itemRefs.current[rightKey]){
      const rr=itemRefs.current[rightKey].getBoundingClientRect();
      if(myCenter>rr.left+rr.width/2){
        const next=[...liveOrder]; next[idx]=rightKey; next[idx+1]=key;
        setLiveOrder(next); drag.current.startX=e.clientX; setDragX(0); return;
      }
    }
    const leftKey=liveOrder[idx-1];
    if(leftKey&&itemRefs.current[leftKey]){
      const lr=itemRefs.current[leftKey].getBoundingClientRect();
      if(myCenter<lr.left+lr.width/2){
        const next=[...liveOrder]; next[idx]=leftKey; next[idx-1]=key;
        setLiveOrder(next); drag.current.startX=e.clientX; setDragX(0);
      }
    }
  };
  const onUp=(key,e)=>{
    if(drag.current.active) e.stopPropagation();
    endDrag(true);
  };
  return(
    <div style={{
      borderRadius:16,overflow:"hidden",marginBottom:12,position:"relative",
      background:"linear-gradient(180deg,#241609 0%,#160D05 100%)",
      boxShadow:"inset 0 2px 10px rgba(0,0,0,.5), 0 4px 16px rgba(0,0,0,.3)",
      border:"1px solid rgba(0,0,0,.4)",
    }}>
      <div style={{position:"relative",padding:"20px 16px 0",background:"radial-gradient(ellipse at 50% 0%, rgba(255,200,140,.10) 0%, transparent 65%)"}}>
        <div style={{display:"flex",alignItems:"flex-end",gap:5,minHeight:114,paddingBottom:2,overflowX:dragKey?"hidden":"auto",scrollbarWidth:"none"}}>
          {ordered.map(item=>(
            <div key={item.key}
              ref={el=>itemRefs.current[item.key]=el}
              onPointerDown={e=>{if(layoutEditMode) onDown(item.key,e);}}
              onPointerMove={e=>{if(layoutEditMode) onMove(item.key,e);}}
              onPointerUp={e=>{if(layoutEditMode) onUp(item.key,e);}}
              onPointerCancel={()=>{if(layoutEditMode) endDrag(false);}}
              onClick={()=>{if(!layoutEditMode&&!shelfDragFlag.justDragged&&onItemClick) onItemClick(item);}}
              style={{cursor:layoutEditMode?"grab":"pointer",minWidth:30,display:"flex",justifyContent:"center",alignItems:"flex-end"}}>
              <BookSpine item={item} isDragging={dragKey===item.key} dragX={dragKey===item.key?dragX:0}/>
            </div>
          ))}
        </div>
      </div>
      <div style={{
        height:15,margin:"0 -1px",
        background:"linear-gradient(180deg,#9A6B3E 0%,#7A4F2E 45%,#5C3A20 100%)",
        boxShadow:"0 5px 10px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.22)",
        backgroundImage:"repeating-linear-gradient(90deg, rgba(0,0,0,.05) 0px, rgba(0,0,0,.05) 1px, transparent 1px, transparent 8px), linear-gradient(180deg,#9A6B3E 0%,#7A4F2E 45%,#5C3A20 100%)",
      }}/>
      <div style={{height:8,background:"linear-gradient(180deg,#160D05,#0A0603)"}}/>
      {(decorations||[]).map(deco=>(
        <FreeDecoration key={deco.id} deco={deco} pos={decorationPositions&&decorationPositions[deco.id]} onMove={onDecorationMove} editable={!!(editable&&layoutEditMode)}/>
      ))}
    </div>
  );
}
